import {fromUnixTime} from "date-fns";
import {format, utcToZonedTime} from "date-fns-tz";
import {APP_DEFAULT_DATE_FORMAT_PIPE, APP_DEFAULT_DATE_TIMEZONE_PIPE} from "../../../../_config/global";

/**
 * @author Carlos Duardo <carlos.duardo@qualud.com>
 */
export class DateHelper {
  public static FORMAT_ISO_STRING = 'yyyy-MM-dd\'T\'HH:mm:ss.SSS\'Z\'';

  public static toDate(
    value?: string | number | null,
    timezone: string | undefined = APP_DEFAULT_DATE_TIMEZONE_PIPE
  ): Date {
    const date = new Date(value ?? Date.now());
    return undefined !== timezone
      ? utcToZonedTime(date, timezone)
      : date
  }

  public static toFormat(
    date: Date,
    pattern: string = APP_DEFAULT_DATE_FORMAT_PIPE,
    timezone: string | undefined = APP_DEFAULT_DATE_TIMEZONE_PIPE
  ): string {

    if (timezone) {
      date = utcToZonedTime(date, timezone)
    }

    return format(
      date,
      pattern,
      {
        timeZone: timezone
      }
    )
  }

  public static toEpoch(
    date: string | Date | null | undefined,
    timezone?: string
  ): number | null {

    if (null === date || undefined === date) return null

    if (typeof date === 'string') {
      date = this.toDate(date, timezone);
    }

    date = timezone ? utcToZonedTime(date, timezone) : date

    return Math.floor(date.getTime() / 1000);
  }

  public static epochToDate(
    epoch: number | null | undefined,
    timezone: string | undefined = APP_DEFAULT_DATE_TIMEZONE_PIPE
  ): Date | null {
    if (null === epoch || undefined === epoch) return null;

    const date = fromUnixTime(epoch);
    return undefined !== timezone
      ? utcToZonedTime(date, timezone)
      : date
  }

  public static epochToFormat(
    epoch: number | null | undefined,
    pattern?: string,
    timezone?: string
  ): string | null {
    if (null === epoch || undefined === epoch) return null;

    return this.toFormat(
      fromUnixTime(epoch),
      pattern,
      timezone
    )
  }

  public static epochToISOString(
    epoch: number | null | undefined,
    timezone?: string
  ): string | null {
    return this.epochToFormat(
      epoch,
      this.FORMAT_ISO_STRING,
      timezone
    )
  }

  public static toISOString(
    value: Date | string | number | null | undefined,
    timezone?: string
  ): string | null {

    const date = value instanceof Date ? value : this.toDate(value);
    return this.toFormat(
      date,
      this.FORMAT_ISO_STRING,
      timezone
    );
  }

}

/**
 * @author Carlos Duardo <carlos.duardo@qualud.com>
 */
export class HelperTools {

  public static isEmpty(value: unknown,  numberGreaterThanZero: boolean = false): boolean {
    const isStringWithData = (val: unknown) => 'string' === typeof val && val.trim().length > 0;
    const isArrayWithData = (val: unknown) => Array.isArray(val) && val.length > 0;
    // @ts-ignore
    const isObjectWithData = (val: unknown) => 'object' === typeof val && Object.keys(val ?? {}).length > 0;
    const isNumberValid = (val: unknown) => {
      if ('number' !== typeof val || !Number.isFinite(val)) {
        return false;
      }

      if (numberGreaterThanZero) {
        return val > 0;
      }

      return true;
    };

    const isBoolean = (val: unknown) => 'boolean' === typeof val;

    return !(isStringWithData(value)
        || isArrayWithData(value)
        || isObjectWithData(value)
        || isNumberValid(value)
        || isBoolean(value)
        )
  }

  public static default(currentValue: unknown, value: any, numberGreaterThanZero: boolean = false): any {
    return HelperTools.isEmpty(currentValue)
      ? value
      : currentValue;
  }

  public static generateHash(
    /**
     * This function generates a random hash string with the specified length and character set options.
     *
     * @param {number} length - The length of the hash string to be generated, by default 10.
     * @param {boolean} hasSymbols - Whether to include symbols in the character set, by default true.
     * @param {boolean} hasNumber - Whether to include numbers in the character set, by default true.
     * @param {boolean} hasLetters - Whether to include letters in the character set, by default true.
     * @param {boolean} lowercase - Whether to include lowercase letters in the character, set by default false.
     * @param {boolean} hasSpecialCharacters - Whether to include special character set, by default false.
     *
     * @returns {string} - The randomly generated hash string.
     */
    options: {
      length?: number,
      hasSymbols?: boolean,
      hasNumber?: boolean,
      lowercase?: boolean
      hasLetters?: boolean
      hasSpecialCharacters?: boolean,
    } = {}) {

    const {
      length = 10,
      hasSymbols = true,
      hasNumber = true,
      lowercase = false,
      hasLetters = true,
      hasSpecialCharacters = false
    } = options


    let charset = '';

    if (hasLetters) {
      charset = 'abcdefghijklmnopqrstuvwxyz';

      if (!lowercase) {
        charset += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      }
    }

    if (hasSymbols) {
      charset += '-!@#$%&*._';
    }
    if (hasSpecialCharacters) {
      charset += 'ñáéíóúç';
    }

    if (hasNumber) {
      charset += '0123456789';
    }

    let retVal = '';
    for (let i = 0, n = charset.length; i < length; ++i) {
      retVal += charset.charAt(Math.floor(Math.random() * n));
    }
    return retVal;
  }

  public static generateSlug({word, upperCase}: { word: string, upperCase?: boolean }) {
    const slug = word
      .replace(/(.)([A-Z][a-z]+)/, '$1_$2')
      .replace(/(a-z0-9)([A-Z])/, '$1_$2')
      .replace(/ /g, '_');

    return upperCase ? slug.toUpperCase() : slug.toLowerCase();
  }

  public static idToIri(endpoint: string, id: number | string): string {
    return `/api/${endpoint}/${id}`;
  }

  public static iriToId(iri: string): number {
    if (iri) {
      const parts = iri.split('/');
      return Number(parts[parts.length - 1]);
    }
    return -1;
  }

  public static getIriEndpoint(iri: string): string | null {
    if (iri) {
      const parts = iri.split('/');
      return parts[2] || null;
    }
    return null;
  }

  public static getRandomInteger(min: number, max: number): number {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1) + min);
  }

  public static getRandomElements<T>(collection: T[], count: number = 1): | T | T[] | null {
    const length = collection.length;

    if (length === 0) {
      return null;
    }

    if (count >= length) {
      return collection;
    }

    if (1 === count) {
      return collection[this.getRandomInteger(0, length - 1)]
    }

    const shuffled = collection.sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  }

  public static extractColumnFromCollection(
    {
      collection,
      column,
      startIndex = null,
      maxResult = null
    }: {
      collection: any[],
      column: string,
      startIndex?: number | null,
      maxResult?: number | null,
    }
  ): any[] {
    let arrayColumValues = collection.map(item => item[column]);

    if (startIndex) {
      arrayColumValues = arrayColumValues.splice(startIndex);
    }

    if (maxResult) {
      arrayColumValues = arrayColumValues.splice(0, maxResult);
    }

    return arrayColumValues;
  }
}
